import React, { useState } from 'react';
import {
    CircularProgress,
    Fab,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useNavigate } from 'react-router';
import { css } from '@emotion/react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { colors } from '@/styles';
import { PrimaryHeader } from '@/styles/typography';
import SearchInput from '../misc/SearchInput';
import Snackbar from '../Snackbar';
import { useCycleCountsList } from './hooks';
import { formatCycleCountDate, formatCycleCountDateTime, formatShipperLabel } from './utils';
import { StatusChip } from './blocks';
import CreateCycleCountModal from './modals/CreateCycleCountModal';

const CycleCounts = () => {
    const navigate = useNavigate();
    const {
        filteredCycleCounts,
        warehouseFilterOptions,
        showSpinner,
        refetchCycleCounts,
        setSearchTerm,
        warehouseFilter,
        setWarehouseFilter,
        dateFromFilter,
        setDateFromFilter,
    } = useCycleCountsList();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [notification, setNotification] = useState({});

    const rows = filteredCycleCounts || [];

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                css={css`
                    padding: 0rem 1rem;
                    border-bottom: 1px solid ${colors.greys.border};
                    background: ${colors.white.primary};
                    align-items: center;
                    height: 56px;
                `}
            >
                <Fab
                    size="medium"
                    color="primary"
                    aria-label="Create cycle count"
                    onClick={() => setShowCreateModal(true)}
                    style={{ width: 25, height: 25, minHeight: 0, boxShadow: 'none' }}
                >
                    <AddIcon style={{ color: '#fff', fontSize: 16 }} />
                </Fab>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    padding: 2rem 1rem 2rem 2rem;
                    align-items: center;
                `}
            >
                <PrimaryHeader>Cycle Count</PrimaryHeader>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    padding: 1rem;
                    margin: 0rem 1rem 2rem 2rem;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    background-color: white;
                    width: auto;
                `}
            >
                <Grid
                    item
                    css={css`
                        flex: 2 1 0;
                    `}
                >
                    <SearchInput
                        css={css`
                            width: 100%;
                        `}
                        placeholder="Search order #, SKU, customer, shipper"
                        onChange={(value) => setSearchTerm(value || '')}
                        size="medium"
                    />
                </Grid>
                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                    `}
                >
                    <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Warehouse"
                        value={warehouseFilter}
                        onChange={(e) => setWarehouseFilter(e.target.value)}
                    >
                        <MenuItem value="">All warehouses</MenuItem>
                        {warehouseFilterOptions.map((warehouse) => (
                            <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                                {warehouse.location_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                    `}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="From date"
                            value={dateFromFilter}
                            onChange={(value) => setDateFromFilter(value)}
                            slotProps={{
                                textField: { fullWidth: true, variant: 'outlined' },
                                field: {
                                    clearable: true,
                                    onClear: () => setDateFromFilter(null),
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
            </Grid>
            <Grid
                container
                css={css`
                    padding: 0rem 1rem 2rem 2rem;
                    flex-grow: 1;
                    justify-content: center;
                    align-items: flex-start;
                `}
            >
                {showSpinner ? (
                    <Grid
                        item
                        css={css`
                            margin-top: 2rem;
                        `}
                    >
                        <CircularProgress color="primary" size="200px" />
                    </Grid>
                ) : (
                    <TableContainer component={Paper} css={css`width: 100%;`}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Warehouse</TableCell>
                                    <TableCell>Shipper</TableCell>
                                    <TableCell>Scheduled Date</TableCell>
                                    <TableCell>Completed</TableCell>
                                    <TableCell align="right"># of Items</TableCell>
                                    <TableCell>Assigned To</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="textSecondary">
                                                No cycle counts found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((count) => (
                                        <TableRow
                                            key={count.cycle_count_id}
                                            hover
                                            css={css`cursor: pointer;`}
                                            onClick={() =>
                                                navigate(`/cycle-counts/${count.cycle_count_id}`)
                                            }
                                        >
                                            <TableCell>
                                                {count.warehouse?.location_name || '-'}
                                            </TableCell>
                                            <TableCell>{formatShipperLabel(count)}</TableCell>
                                            <TableCell>{formatCycleCountDate(count.date)}</TableCell>
                                            <TableCell>
                                                {formatCycleCountDateTime(count.completed_at)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {count.expectedItemCount ?? 0}
                                            </TableCell>
                                            <TableCell>
                                                {count.assigned_teammate?.username || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <StatusChip status={count.status} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Grid>
            <Snackbar
                open={!!notification?.message}
                {...notification}
                handleClose={() => setNotification({})}
            />
            <CreateCycleCountModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => {
                    refetchCycleCounts?.().catch(() => {});
                }}
                onNotify={setNotification}
            />
        </Grid>
    );
};

export default CycleCounts;
